import { Component, OnInit, OnChanges, Input, Output, ViewEncapsulation, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import Def from 'autocomplete-lhc'; // see docs at http://lhncbc.github.io/autocomplete-lhc/docs.html
import copy from "fast-copy";
import { LhcDataService} from '../../../lib/lhc-data.service';
import CommonUtils from "../../../lib/lforms/lhc-common-utils.js";
import language from "../../../../language-config.json";

@Component({
    selector: 'lhc-autocomplete',
    templateUrl: './lhc-autocomplete.component.html',
    styleUrls: ['./lhc-autocomplete.component.css'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class LhcAutocompleteComponent implements OnChanges, AfterViewInit, OnDestroy {

  // autocomplete-lhc options:
  //   .elementId
  //   .readOnly
  //   .placeholder
  //   .acOptions
  @Input() options: any;
  @Input() item:any;
  @Input() dataModel: any;
  @Input() isFormReady: boolean;
  @Output() dataModelChange: EventEmitter<any>  = new EventEmitter<any>();

  // emit the 'focus' and 'blur' events on the input fields
  @Output() onFocusFn: EventEmitter<any> = new EventEmitter<any>();
  @Output() onBlurFn: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("ac") ac: ElementRef<any>;

  selectedItems: any[] = [];
  multipleSelections: boolean = false;
  allowNotOnList: boolean = false;
  acType: string = null;
  acInstance: any = null;
  prefetchTextToItem: Record<string, any> = Object.create(null);
  displayProp: string = '';
  viewInitialized = false;
  autocompleteInvalidError = language.invalidAnswer;
  private invalidTextRestoreTimeout: ReturnType<typeof setTimeout> | null = null;
  private canonicalSelectionCleanupTimeout: ReturnType<typeof setTimeout> | null = null;
  private autocompleteLifecycleId = 0;

  constructor(
    public lhcDataService: LhcDataService
  ) {}

  /**
   * Invokded when the properties change
   * Reset the defualt settings
   * @param changes changes.prop contains the old and the new value
   */
  ngOnChanges(changes) {

    if (this.viewInitialized) {
      let dataChanged:boolean;
      // answer list and data model both change, both the answer list and values are changed by fhirpath expressions or data controls.
      if (changes.options && changes.dataModel) {
        this.cleanupAutocomplete(true);
        this.setupAutocomplete();
        dataChanged = changes.dataModel && !CommonUtils.deepEqual(this.dataModel, changes.dataModel.previousValue);
      }
      // a new answer list
      else if (changes.options) {
        const {keep, dataModelChanged}  = this.keepDataModel(changes);
        this.cleanupAutocomplete(keep);
        this.setupAutocomplete();
        dataChanged = dataModelChanged || !keep;
      }
      // data model changes, from outside of ac by fhirpath expressions or data controls
      else if (changes.dataModel) {
        // update the selected value in the autocomplete field
        this.updateDisplayedValue(this.dataModel)
        dataChanged = true;
      }

      // run the change function
      if (dataChanged) {
        this.dataModelChange.emit(this.dataModel);
        this.lhcDataService.onItemValueChange(this.item, null, null, true)
      }
    }
  }


  /**
   *  Decides whether the data model should be preserved when reconstructing the autocomplete-lhc widget.
   * @param changes the changes object passed to ngOnChanges
   * @returns {{keep: boolean, dataModelChanged: boolean|undefined}} flags indicating whether the data model should be kept and whether it changed.
   */
   keepDataModel(changes) {
    let keep, dataModelChanged;
    // need to keep the dataModel while cleaning up the previous autocomplete,
    // when the form is initially rendered with the FHIR resoruce data loaded,
    // asynchronously. At this moment the saved the user data are already in the
    // data model and the autocomplete is already set up, with potentially an
    // empty list if the answer list is loaded (asynchronously) by FHIRPath
    // expressions or data controls.
    // In the RxTerms form with saved data, the list for the saved drug
    // strength is loaded after an empty list has been set, and
    // changes.isFormReady.previousValue is false, but
    // isFormReady.currentValue is true.  In that case we need to keep the data
    // model.

    // let isFormReady = changes.isFormReady?.previousValue === false && changes.isFormReady?.currentValue === true;
    const isFormReady = changes.isFormReady?.previousValue !== undefined ? changes.isFormReady.previousValue :
      this.isFormReady ;
    // if we are in the situation described above when form first loads.

    if (isFormReady) {
      // The form is ready.  Check whether the list has changed.
      const prevOpts = changes?.options?.previousValue?.acOptions || {};
      const currentOpts = changes?.options?.currentValue?.acOptions || {};
      let prevConfig, currentConfig;
      if (prevOpts.listItems !== undefined || currentOpts.listItems !== undefined) {
        // First case: prefetched lists, defined with 'listItems'
        prevConfig = prevOpts.listItems;
        currentConfig = currentOpts.listItems;
      }
      else if (prevOpts.url !== undefined || currentOpts.url !== undefined) {
        // Second case: search lists, defined with 'url'
        prevConfig = prevOpts.url;
        currentConfig = currentOpts.url;
      }
      else if (prevOpts.fhir !== undefined || currentOpts.fhir !== undefined) {
        // Third case: search lists, defined with 'fhir'
        prevConfig = prevOpts.fhir;
        currentConfig = currentOpts.fhir;
      }
      keep = CommonUtils.deepEqual(prevConfig, currentConfig);

      // special case for maxSelect
      if (prevOpts.maxSelect !== currentOpts.maxSelect) {
        // if only maxSelect (repeats) changes, keep the current data model
        // multiple selection changes to single selection
        if ((prevOpts.maxSelect === "*" ||  parseInt(prevOpts.maxSelect)> 1) &&
            parseInt(currentOpts.maxSelect) === 1) {
          this.dataModel = this.dataModel[0];
          dataModelChanged = true;
        }
        // single selection changes to mulitple selection
        else if (parseInt(prevOpts.maxSelect) === 1 &&
          (currentOpts.maxSelect === "*" ||  parseInt(currentOpts.maxSelect)> 1)) {
          this.dataModel = [this.dataModel];
          dataModelChanged = true;
        }
        keep = true;
      }
    }
    else {
      // If the form was not ready, then the list might just be getting set, so
      // we keep the data model.
      keep = true;
    }
    return {keep, dataModelChanged};
  }

  /**
   * Update the display value of the autocompleter when the item.value is changed
   * changed by data control or fhirpath expression after the autocompleter is initialized
   * @param itemValue the new data in item.value
   */
  updateDisplayedValue(itemValue:any) {
    // Note:  This runs both in response to user interaction and to JavaScript
    // changes.
    if (!this.multipleSelections) {
      this.removeAutocompleteValidationError();
      if (!itemValue) {
        this.clearAutocompleteInvalidState();
        this.acInstance.setFieldVal('', false);
      }
      else {
        const dispVal = this.updateAutocompSelectionModel(itemValue);
        if (typeof dispVal === 'string') {
          const fieldVal = this.acType === "prefetch" ? dispVal.trim() : dispVal;
          this.acInstance.setFieldVal(fieldVal, false);
          if (this.modelValueMatchesAutocompleteList(itemValue, fieldVal)) {
            this.clearAutocompleteInvalidState();
          }
        }
        else {// handle the case of an empty object as a model
          this.acInstance.setFieldVal('', false);
          this.clearAutocompleteInvalidState();
        }
      }
    }
    else {  // multi-select (here handling calculated values)
      this.acInstance.clearStoredSelection();
      if (Array.isArray(itemValue)) {
        for (const v of itemValue) {
          const dispVal = this.updateAutocompSelectionModel(v);
          this.acInstance.addToSelectedArea(dispVal);
        }
      }
      this.updateAutocompleteValidationError();
    }
  }


  /**
   * Checks whether a model value represents an autocomplete list selection.
   * @param itemValue the model value to check.
   * @param displayValue the value displayed in the autocomplete field.
   * @returns whether the value is known to be on the autocomplete list.
   */
  modelValueMatchesAutocompleteList(itemValue: any, displayValue: string): boolean {
    return this.acType === 'prefetch' ?
      this.getCanonicalPrefetchText(displayValue) !== null :
      !itemValue?._notOnList;
  }


  /**
   *  Returns the display value of an answer.
   * @param answer an object with the data for one selected answer
   * @returns {string|null} the answer's display text, or null if no display text can be determined.
   */
  getDisplayValue(answer) {

    let displayValue = null;

    if (typeof answer === 'string') {
      displayValue = answer;
    }
    // an answer in a prefetch answer list
    else if (this.acType === "prefetch") {
      if (!answer._notOnList) {
        // answer is an item in the listItemsForModel if there is an listItemsForModel
        if (this.options.acOptions.listItemsForModel) {
          for (let i=0, iLen = this.options.acOptions.listItemsForModel.length; i<iLen; i++) {
            if (CommonUtils.deepEqual(answer, this.options.acOptions.listItemsForModel[i])) {
              // get the display text from the modified answer list (listItems)
              displayValue = this.options.acOptions.listItems[i][this.displayProp];
              if (this.options.acOptions.isListHTML) {
                displayValue = displayValue.replace(/(<([^>]+)>)/gi, "").trim();
              }
              break;
            }
          }
        }
        // there is no listItemsForModel or no listItems
        else {
          displayValue = answer[this.displayProp] ? answer[this.displayProp]: answer.text;
        }
      }
      // not-on-list, coding and string
      else if (answer._notOnList) {
        displayValue = answer.text;
      }
    }
    // search
    else {
      displayValue = answer[this.displayProp];
    }

    return displayValue;

    // return typeof answer === 'string' ? answer :
    //   this.acType === "prefetch" && !answer._notOnList ? answer[this.displayProp] : answer.text;

  }


  /**
   *  Updates the autocompleter's model to register an item as selected.
   * @param answer an object with data (possibly with a code) for the answer
   * @returns {string|null} the display text determined for the answer.
   */
  updateAutocompSelectionModel(answer) {
    const dispVal = this.getDisplayValue(answer);
    this.acInstance.storeSelectedItem(dispVal, answer.code);
    return dispVal;
  }


  /**
   * Initialize the autocomplete-lhc
   * Cannot be done in ngOnInit because the DOM elements that autocomplete-lhc depends on are
   * not ready yet on ngOnInit
   */
  ngAfterViewInit() {
    // initial setup with default/initial value or values from QR
    const oldDataModel = CommonUtils.deepCopy(this.dataModel);
    this.setupAutocomplete();
    if (!CommonUtils.deepEqual(oldDataModel, this.dataModel)) {
      this.dataModelChange.emit(this.dataModel);
      this.lhcDataService.onItemValueChange(this.item, null, null, true)
    }
    this.viewInitialized = true;
  }


  /**
   * Clean up the autocompleter instance
   */
  ngOnDestroy() {
    this.cleanupAutocomplete();
  }


  /**
   * Input field blur event handler
   */
  onInputBlur() {
    this.onBlurFn.emit();
    setTimeout(() => this.updateAutocompleteValidationError());
  }


  /**
   * Input field focus event handler
   */
  onInputFocus() {
    this.onFocusFn.emit();
  }


  /**
   * Adds/removes the LForms validation message for autocomplete-lhc's own
   * invalid typed-value state. The normal LForms validation path does not see
   * this because an unmatched typed value is not written to item.value.
   */
  updateAutocompleteValidationError(): void {
    if (!this.item || !this.ac?.nativeElement) {
      return;
    }
    const input = this.ac.nativeElement;

    if (!input.value) {
      this.removeAutocompleteValidationError();
      this.clearAutocompleteInvalidState();
      return;
    }

    const isInvalid = input.classList.contains('invalid') ||
      input.getAttribute('invalid') === 'true';

    if (isInvalid) {
      this.addAutocompleteValidationError();
    }
    else {
      this.removeAutocompleteValidationError();
      // A non-invalid value can still intentionally be off-list for an
      // optionsOrString field. Preserve autocomplete-lhc's no_match state in
      // that case; otherwise synchronize its state as a valid list match.
      if (!input.classList.contains('no_match')) {
        this.clearAutocompleteInvalidState();
      }
    }
  }


  /**
   * Adds the autocomplete-lhc typed-value error if it is not already present.
   */
  addAutocompleteValidationError(): void {
    if (!this.item) {
      return;
    }
    this.item._showValidation = true;
    this.item._hasAutocompleteValidationError = true;
    const errors = this.item._validationErrors || [];

    if (!errors.includes(this.autocompleteInvalidError)) {
      this.item._validationErrors = [...errors, this.autocompleteInvalidError];
    }
  }


  /**
   * Removes only the autocomplete-lhc typed-value error, preserving any other
   * validation messages on the item.
   */
  removeAutocompleteValidationError(): void {
    if (this.item) {
      delete this.item._hasAutocompleteValidationError;
    }
    if (Array.isArray(this.item?._validationErrors)) {
      this.item._validationErrors = this.item._validationErrors
        .filter(error => error !== this.autocompleteInvalidError);
      if (this.item._validationErrors.length === 0) {
        delete this.item._validationErrors;
      }
    }
  }


  /**
   * Clears autocomplete-lhc's typed-value invalid markers from the input.
   */
  clearAutocompleteInvalidState(): void {
    const input = this.ac?.nativeElement;
    if (!input) {
      return;
    }

    this.acInstance?.setInvalidValIndicator?.(false);
    this.acInstance?.setMatchStatusIndicator?.(true);

    // Keep the DOM normalized when an instance is being torn down or a test
    // double does not implement autocomplete-lhc's stateful methods.
    input.classList.remove('invalid', 'no_match');
    input.removeAttribute('invalid');
  }


  /**
   * Returns the canonical prefetch list text matching the typed value.
   * @param typedValue the text typed into the autocomplete input.
   * @returns {string|null} the matching list text, or null if there is no match.
   */
  getCanonicalPrefetchText(typedValue: string): string | null {
    const trimmedValue = typedValue?.trim();
    if (!trimmedValue || this.acType !== 'prefetch') {
      return null;
    }

    if (this.hasPrefetchItem(trimmedValue)) {
      return trimmedValue;
    }

    if (!this.isCaseInsensitiveSelectionEnabled()) {
      return null;
    }

    const lowerCaseValue = trimmedValue.toLowerCase();
    return Object.keys(this.prefetchTextToItem)
      .find(text => text.toLowerCase() === lowerCaseValue) || null;
  }


  /**
   * Checks whether the prefetch mapping itself contains the given text.
   * @param text the display text to look up.
   * @returns {boolean} whether text is an own key in the prefetch mapping.
   */
  hasPrefetchItem(text: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.prefetchTextToItem, text);
  }


  /**
   * autocomplete-lhc defaults case-insensitive typed selection to true.
   * @returns {boolean} true unless case-insensitive typed selection is explicitly disabled.
   */
  isCaseInsensitiveSelectionEnabled(): boolean {
    return this.options?.acOptions?.caseInsenstiveSelection !== false;
  }


  /**
   * Applies autocomplete-lhc's canonical prefetch list value for a typed match.
   * @param canonicalText the matching list text to select.
   */
  selectCanonicalPrefetchText(canonicalText: string): void {
    if (!canonicalText || !this.acInstance || !this.hasPrefetchItem(canonicalText)) {
      return;
    }

    const selectedAnswer = this.prefetchTextToItem[canonicalText];
    const alreadySelected = this.multipleSelections &&
      this.acInstance.isSelected?.(canonicalText);
    this.acInstance.setFieldVal(canonicalText, false);
    if (!alreadySelected) {
      this.acInstance.storeSelectedItem(canonicalText, selectedAnswer?.code);
      if (this.multipleSelections) {
        this.acInstance.addToSelectedArea(canonicalText);
      }
    }
    if (this.multipleSelections) {
      this.scheduleCanonicalSelectionCleanup();
    }
    this.clearAutocompleteInvalidState();
  }


  /**
   * Clears the pending field after a case-insensitive multi-select match.
   * autocomplete-lhc finishes its non-list handling after notifying selection
   * observers, so this must run after that handler has returned.
   */
  private scheduleCanonicalSelectionCleanup(): void {
    this.cancelCanonicalSelectionCleanup();
    const acInstance = this.acInstance;
    const input = this.ac?.nativeElement;
    const acElement = acInstance?.element;
    const lifecycleId = this.autocompleteLifecycleId;

    this.canonicalSelectionCleanupTimeout = setTimeout(() => {
      this.canonicalSelectionCleanupTimeout = null;
      if (this.autocompleteLifecycleId !== lifecycleId ||
          this.acInstance !== acInstance || this.ac?.nativeElement !== input ||
          acInstance?.element !== acElement) {
        return;
      }

      acInstance.setFieldVal('', false);
      acInstance.processedFieldVal_ = '';
      acInstance.fieldValIsListVal_ = true;
      this.removeAutocompleteValidationError();
      this.clearAutocompleteInvalidState();
    });
  }

  /**
   * Clean up the autocompleter if there is one
   * @param keepDataModel whether to keep the data model value on the autocompleter. default is false.
   */
  cleanupAutocomplete(keepDataModel:boolean=false): void {
    this.autocompleteLifecycleId++;
    this.cancelInvalidTextRestore();
    this.cancelCanonicalSelectionCleanup();
    this.removeAutocompleteValidationError();
    this.clearAutocompleteInvalidState();
    if (this.acInstance) {
      // reset the field value
      this.acInstance.setFieldVal('', false);
      // reset the data model value
      if (!keepDataModel && this.dataModel !==undefined) {
        this.dataModel = null;
      }
      this.acInstance.destroy();
    }
  }


  /**
   * Cancels a pending restoration of invalid input text.
   */
  private cancelInvalidTextRestore(): void {
    if (this.invalidTextRestoreTimeout !== null) {
      clearTimeout(this.invalidTextRestoreTimeout);
      this.invalidTextRestoreTimeout = null;
    }
  }


  /**
   * Cancels pending cleanup for a normalized multi-select entry.
   */
  private cancelCanonicalSelectionCleanup(): void {
    if (this.canonicalSelectionCleanupTimeout !== null) {
      clearTimeout(this.canonicalSelectionCleanupTimeout);
      this.canonicalSelectionCleanupTimeout = null;
    }
  }


  /**
   * Set up the autocompleter
   */
  setupAutocomplete(): void {
    // reset status
    this.selectedItems = [];
    this.multipleSelections = false;
    this.acType = null;
    this.acInstance = false;
    this.allowNotOnList = null;
    this.prefetchTextToItem = Object.create(null);
    this.displayProp = "";

    // if there are options for the autocompleter
    if (this.options && this.options.acOptions) {
      const acOptions = this.options.acOptions;
      if (acOptions.maxSelect && (acOptions.maxSelect === "*" || parseInt(acOptions.maxSelect) > 1)) {
        this.multipleSelections = true;
      }
      this.allowNotOnList = !acOptions.matchListValue;
      this.displayProp = acOptions.display || "text";

      // search autocomplete
      if (acOptions.hasOwnProperty('url') || (acOptions.fhir && acOptions.fhir.search)) {
        this.acType = 'search';
        this.acInstance = new Def.Autocompleter.Search(this.ac.nativeElement, acOptions.url, acOptions);
      }
      // prefetch autocomplete
      else { // whether or not there are currently list items
        this.acType = 'prefetch';
        const listItemsText = [];
        // get a list of display text, code and create a answer text to answer item mapping.
        // (autocomplete-lhc requires answer text to be unique)
        acOptions.listItems.forEach((item, index) => {
          listItemsText.push(item[this.displayProp]);
          let displayPropText = item[this.displayProp].trim();
          if (acOptions.isListHTML) {
            displayPropText = displayPropText.replace(/(<([^>]+)>)/gi, "").trim();
          }
          this.prefetchTextToItem[displayPropText] = acOptions.listItemsForModel ? acOptions.listItemsForModel[index] : item;
        }, this);

        // acOptions has matchListValue, maxSelected, codes
        // Using this.options.elementId causes the autocompleter to be refreshed without an autocompleter created in a horizontal table.
        // (where the rows lists are created as a new array, instead of keeping the same reference. Not confirmed, but I suspect this is the reason.)
        // It works with vertical layout though.
        // Using this.ac.nativeElement works in both cases.
        this.acInstance = new Def.Autocompleter.Prefetch(this.ac.nativeElement, listItemsText, acOptions);
      }

      this.retainInvalidValueOnBlur();

      const defaultItem =  acOptions.defaultValue
      // set up initial values if there is value
      const savedValue = this.dataModel || defaultItem;  //boolean is not a valid data type for answerOption
      this.setItemInitValue(savedValue)

      // add event handler
      Def.Autocompleter.Event.observeListSelections(this.options.elementId, this.onSelectionHandler.bind(this));
    }
  }


  /**
   * Keeps autocomplete-lhc from automatically clearing invalid typed text.
   * LForms retains the text so the user can correct it while the underlying
   * model remains unchanged.
   */
  retainInvalidValueOnBlur(): void {
    const acInstance = this.acInstance;
    if (typeof acInstance?.clearInvalidFieldVal !== 'function') {
      return;
    }

    acInstance.clearInvalidFieldVal = () => undefined;
  }


  /**
   * Set the initial item.value to the autocompleter when the autocompleter is being set up or
   * the item.value is changed later
   * @param itemValue the value to display and store in the autocompleter.
   */
  setItemInitValue(itemValue) {
    if (itemValue) {
      if (this.multipleSelections && Array.isArray(itemValue)) {
        for (let i=0, len=itemValue.length; i<len; ++i) {
          const v = itemValue[i];
          const dispVal = this.getDisplayValue(v);
          this.acInstance.storeSelectedItem(dispVal, v.code);
          this.acInstance.addToSelectedArea(dispVal);
        }
        // Clear the field value for multi-select lists
        this.acInstance.setFieldVal('', false);

        this.selectedItems = itemValue;
      }
      else {
        const dispVal = this.getDisplayValue(itemValue);
        if (typeof dispVal === 'string') {
          this.acInstance.storeSelectedItem(dispVal, itemValue.code);
          const fieldVal = this.acType === "prefetch" ? dispVal.trim() : dispVal;
          this.acInstance.setFieldVal(fieldVal, false);
        }
        else {// handle the case of an empty object as a model
          this.acInstance.setFieldVal('', false);
        }
        this.dataModel = itemValue;
      }
    }
  }


  /**
   * Event handler for when an answer item is selected
   * @param event the event emitted from an autocompleter
   */
  onSelectionHandler(event) {
    let changed = false;
    const eventFinalValue = event?.final_val || '';
    const canonicalPrefetchText = this.getCanonicalPrefetchText(eventFinalValue);
    const input = this.ac?.nativeElement;
    const removedWithInvalidPendingValue = event?.removed && input?.value &&
      (input.classList.contains('invalid') || input.getAttribute('invalid') === 'true' ||
        this.item?._hasAutocompleteValidationError);
    const isInvalidAutocompleteValue = !event?.removed && !event?.on_list &&
      eventFinalValue &&
      this.options?.acOptions?.matchListValue &&
      !canonicalPrefetchText;

    if (isInvalidAutocompleteValue) {
      const input = this.ac?.nativeElement;
      const invalidInputValue = input?.value || eventFinalValue;
      const acInstance = this.acInstance;
      const acElement = acInstance?.element;
      const lifecycleId = this.autocompleteLifecycleId;
      this.cancelInvalidTextRestore();

      // A single-select field must not retain a previously selected answer when
      // the input now displays a different, invalid value. Multi-select answers
      // remain visible as selected tags, so an invalid pending value does not
      // clear those selections.
      if (!this.multipleSelections && this.dataModel !== null && this.dataModel !== undefined) {
        this.dataModel = null;
        this.selectedItems = null;
        this.dataModelChange.emit(this.dataModel);
        this.lhcDataService.onItemValueChange(this.item, null, null, true);
      }

      // Emitting the model change and running its rules can synchronously rebuild
      // this autocomplete or destroy the component. Do not schedule work for an
      // instance that is no longer current.
      if (this.autocompleteLifecycleId !== lifecycleId ||
          this.acInstance !== acInstance || this.ac?.nativeElement !== input ||
          acInstance?.element !== acElement) {
        return;
      }

      // Updating the model can cause Angular to refresh the input from null.
      // Restore the invalid text after that refresh so the user can correct it.
      this.invalidTextRestoreTimeout = setTimeout(() => {
        this.invalidTextRestoreTimeout = null;
        if (this.autocompleteLifecycleId !== lifecycleId ||
            this.acInstance !== acInstance || this.ac?.nativeElement !== input ||
            acInstance?.element !== acElement) {
          return;
        }
        if (!this.multipleSelections && this.dataModel == null && invalidInputValue) {
          acInstance?.setFieldVal(invalidInputValue, false);
          input?.classList.add('invalid', 'no_match');
          input?.setAttribute('invalid', 'true');
        }
        this.updateAutocompleteValidationError();
      });
      return;
    }
    else if (!event?.removed) {
      this.removeAutocompleteValidationError();
      if (event?.on_list || canonicalPrefetchText) {
        this.clearAutocompleteInvalidState();
      }
    }

    if (!event?.on_list && canonicalPrefetchText) {
      this.selectCanonicalPrefetchText(canonicalPrefetchText);
    }

    if (this.acType === 'prefetch') {
      const selectedTexts = this.acInstance.getSelectedItems()
      changed = this.setItemValueForPrefetchAC(selectedTexts);
    }
    else if (this.acType === 'search') {
      changed = this.setItemValueForSearchAC(event);
    }

    if (changed) {
      if (!isInvalidAutocompleteValue && !event?.removed) {
        this.removeAutocompleteValidationError();
      }
      // run the change function
      this.dataModelChange.emit(this.dataModel);
      this.lhcDataService.onItemValueChange(this.item, null, null, true)
    }

    if (event?.removed) {
      setTimeout(() => {
        const input = this.ac?.nativeElement;
        if (removedWithInvalidPendingValue && input?.value) {
          input.classList.add('invalid', 'no_match');
          input.setAttribute('invalid', 'true');
        }
        this.updateAutocompleteValidationError();
      });
    }

  }


  /**
   * Set up an item's value when an answer is selected from a 'prefetch' autocompleter
   * @param selectedTexts the selected answer's text
   * @returns {boolean} whether item's value has changed
   */
  setItemValueForPrefetchAC(selectedTexts: string[]): boolean {
    const currentValue = copy(this.dataModel);
    if (selectedTexts) {
      if (selectedTexts.length === 0) {
        this.dataModel = null;
      }
      else {
        const selectedAnswerItems = selectedTexts.map(text => {
          if (this.hasPrefetchItem(text)) {
            return this.prefetchTextToItem[text];
          }
          else if (this.allowNotOnList) {
            return this.options.modelForOffListItem ? this.options.modelForOffListItem(text) :
              {"text" : text, "_notOnList": true};
          }
          else {
            // do nothing. this should not happen?
          }
        });
        // mulitple selection
        if (this.multipleSelections) {
          this.dataModel = selectedAnswerItems;
        }
        // single selection
        else if (selectedAnswerItems.length >=0 && selectedAnswerItems[0]) {
          this.dataModel = selectedAnswerItems[0]
        }
        else {
          this.dataModel = null;
        }
      }
    }
    else {
      this.dataModel = null;
    }

    return CommonUtils.deepEqual(currentValue, this.dataModel) ? false: true;
  }


  /**
   * Get the selected answer object from a 'search' autocompleter
   * @param itemText answer's text
   * @param onList whether the answer's text matches the answers texts on the list
   * @returns {Object|null} an answer object with 'code_system' renamed to 'system' if present, or null.
   */

  getSearchItemModelData(itemText, onList) {
    let rtn =null;
    if (itemText !== '') {
      const item = this.acInstance.getItemData(itemText);
      if (onList) {
        rtn = item;
      }
      else if (!onList && this.allowNotOnList) {
        rtn = item;
        rtn._notOnList = true;
      }
      // For answerValueSet, when 'questionnaire-itemControl' is set to be 'autocomplete',
      // it is a search field in lforms.
      // The 'system' value is returned in 'code_system' from the autocompleter.
      // Convert code_system to system
      if (rtn && rtn.code_system && !rtn.system) {
        rtn.system = rtn.code_system;
        delete rtn.code_system;
      }
    }
    return rtn;
  }


  /**
   * Set up an item's value when an answer is selected from a 'search' autocompleter
   * @param eventData the list selection event emitted from the search autocompleter.
   * @returns {boolean} whether the item's value has changed
   */
  setItemValueForSearchAC(eventData):boolean {
    const itemText = eventData.final_val;
    const onList = eventData.on_list;
    const currentValue = copy(this.dataModel);
    if (this.acType === 'search') {
      // single selection
      if (!this.multipleSelections) {
        this.selectedItems = this.getSearchItemModelData(itemText, onList);
      }
      // multiple selection
      else {
        // remove the item
        if (eventData.removed) {
          // (create a new array so that change detection is triggered)
          this.selectedItems = this.selectedItems.filter(item => itemText !==item.text)
        }
        // new item, in the search result
        else {
          const newItem = this.getSearchItemModelData(itemText, onList);
          if (newItem) {
            // add the new item
            // (create a new array so that change detection is triggered)
            this.selectedItems = [...this.selectedItems, newItem];
          }
        }
      }
    }
    this.dataModel = this.selectedItems;

    return CommonUtils.deepEqual(currentValue, this.dataModel) ? false: true;
  }
}
